
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReportData } from '@/types/report';
import { formatDate, formatTime, getProblemsEncountered, getSolutionsApplied, parseDevelopmentReport } from '@/utils/reportFormatters';

type ReportDetailsTableProps = {
  report: ReportData;
}

export const ReportDetailsTable: React.FC<ReportDetailsTableProps> = ({ report }) => {
  const { t } = useTranslation();
  // Get development data if available
  const developmentData = parseDevelopmentReport(report);
  
  // Helper function to get value from either parsed development data or direct field
  const getValue = (fieldName: string): string => {
    if (developmentData && developmentData[fieldName] !== undefined) {
      return developmentData[fieldName] || '-';
    }
    
    // Special case for nested fields
    if (fieldName in report) {
      return (report as any)[fieldName] || '-';
    }
    
    return '-';
  };
  
  // Define fields to display with their labels
  const reportFields = [
    { label: t('reports.details.studentName'), value: `${report.student_nickname} (${report.student_full_name})` },
    { label: t('reports.details.grade'), value: report.student_grade_level || '-' },
    { label: t('reports.details.subject'), value: report.subject || '-' },
    { label: t('reports.details.teacher'), value: report.teacher_display || report.teacher_id || '-' },
    { label: t('reports.details.teachingDate'), value: formatDate(report.teaching_date) || '-' },
    { label: t('reports.details.teachingTime'), value: report.start_date && report.end_date ? 
      `${formatTime(report.start_date)} - ${formatTime(report.end_date)}` : 
      report.teaching_time_slot || '-' 
    },
    { label: t('reports.details.teachingDuration'), value: report.class_duration ? `${report.class_duration} ${t('reports.details.hoursUnit')}` : report.teaching_duration || '-' },
    { label: t('reports.details.location'), value: report.teaching_location || '-' },
    { label: t('reports.details.teachingType'), value: report.teaching_type || '-' },
    
    // Topic learned - from either development data or direct field
    { label: t('reports.details.topicLearned'), value: getValue('topic_learned') },
    { label: t('reports.details.teachingContent'), value: report.teaching_content || '-' },
    
    // Assessment fields - from development data
    { label: t('reports.details.knowledgeRating'), value: getValue('knowledge_rating') },
    { label: t('reports.details.knowledgeBehavior'), value: getValue('knowledge_behavior') },
    { label: t('reports.details.skillsRating'), value: getValue('skills_rating') },
    { label: t('reports.details.skillsBehavior'), value: getValue('skills_behavior') },
    { label: t('reports.details.attitudeRating'), value: getValue('attitude_rating') },
    { label: t('reports.details.attitudeBehavior'), value: getValue('attitude_behavior') },
    
    // Development and problems
    { label: t('reports.details.improvement'), value: getValue('student_improvement') },
    { label: t('reports.details.problems'), value: getProblemsEncountered(report) },
    { label: t('reports.details.solutions'), value: getSolutionsApplied(report) },
    
    // Homework and next class
    { label: t('reports.details.homework'), value: report.homework_assigned || report.homework_assignment || '-' },
    { label: t('reports.details.nextTopic'), value: report.next_topic || report.next_class_preparation || '-' },
    
    // Submission details
    { label: t('reports.details.reportStatus'), value: report.status || '-' },
    { label: t('reports.details.submittedOnTime'), value: report.submitted_on_time === false ? t('reports.details.no') : t('reports.details.yes') },
    { label: t('reports.details.extraNote'), value: report.note || '-' },
    { label: t('reports.details.rejectionReason'), value: report.rejection_reason || '-' },
  ];

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold">{t('reports.details.title')}</h3>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <div className="grid grid-cols-1 gap-2">
          {reportFields.map((field, index) => (
            <div key={index} className="grid grid-cols-3 py-2 border-b border-gray-100 last:border-0">
              <div className="col-span-1 font-medium text-gray-700">{field.label}</div>
              <div className="col-span-2 break-words">{field.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
